/*
 * Part Table
 */

import * as React from 'react';
import { useState, useEffect, useMemo } from 'react';

import {
    Itile,
    IinstallBasePart,
    IinstallBasePartPopulated,
    Ijob,
    IjobPart,
    IworkOrderPart,
    IWorkOrder
} from '../../models';
import { useTable, Column, useFlexLayout } from 'react-table';
import { toastr } from 'react-redux-toastr';

import { TFunction } from 'i18next';
import { TableUtil } from '../common/TableUtil';
import { constants } from '../../constants/constants';
import { Button } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    clearSelectedInstallBasePartID,
    deleteInstallBasePart,
    setSelectedInstallBasePartID
} from '../../actions/partsActions';
import { isJobEditable } from '../../utils/reviewFlow';

interface Iprops {
    tableData: IinstallBasePartPopulated[] | IinstallBasePart[];
    t: TFunction;
    loading: boolean;
    isUS: boolean;
    currentTile: Itile;
    selectedInstallBasePartID: string;
    deleteInstallBasePart: typeof deleteInstallBasePart;
    setSelectedInstallBasePartID: typeof setSelectedInstallBasePartID;
    clearSelectedInstallBasePartID: typeof clearSelectedInstallBasePartID;
    disabled: boolean;
    selectedJob: Ijob;
    jobParts: { [key: string]: IjobPart };
    workOrderParts: { [key: string]: IworkOrderPart };
    workOrders: { [key: string]: IWorkOrder };
}

const PartTable: React.FC<Iprops> = props => {
    const {
        tableData,
        t,
        currentTile,
        selectedJob,
        disabled,
        deleteInstallBasePart: deletePart,
        setSelectedInstallBasePartID: setSelectedPartID,
        jobParts,
        workOrderParts,
        workOrders
    } = props;

    const [selectedRow, setSelectedRow] = useState<number | null>(null);

    // Reset selected row when selectedInstallBasePartID changes
    useEffect(() => {
        if (props.selectedInstallBasePartID.length === 0) {
            setSelectedRow(null);
        }
    }, [props.selectedInstallBasePartID]);

    const handleDelete = (item: IinstallBasePart) => {
        if (disabled) {
            return;
        }

        const toastrConfirmOptions = {
            onOk: () => {
                deletePart(item.id);
            },
            onCancel: () => null,
            okText: t('deletePartConfirmButton'),
            cancelText: t('common:cancel')
        };
        toastr.confirm(t('deletePartConfirmTitle'), toastrConfirmOptions);
    };

    // Build columns using useMemo for performance
    const columns = useMemo(() => {
        return TableUtil.translateHeaders(
            [
                {
                    Header: () => <span>{String(t('numberHeader'))}</span>,
                    id: 'number',
                    accessor: (item: IinstallBasePartPopulated) => {
                        // Get the estimated quantity from the original job part or work order part
                        let estimatedQuantity = 0;

                        if (item.jobPartID) {
                            const jobPart = jobParts[item.jobPartID];
                            estimatedQuantity = jobPart?.estimated || 0;
                        } else if (item.workOrderPartID) {
                            const workOrderPart = workOrderParts[item.workOrderPartID];
                            estimatedQuantity = workOrderPart?.estimated || 0;
                        }
                        // For direct parts and stock parts, there's no "estimated" quantity concept
                        // so we show just the part number without quantity

                        return estimatedQuantity > 0 ? (
                            <span>{`(${estimatedQuantity}) ${item.part?.number}`}</span>
                        ) : (
                            <span>{item.part?.number}</span>
                        );
                    },
                    minWidth: 80
                },
                {
                    Header: 'descriptionHeader',
                    id: 'description',
                    accessor: (item: IinstallBasePartPopulated) => {
                        if (item.partStockID) {
                            return <span>{`${item.part.description} - [boot stock]`}</span>;
                        } else {
                            return <span>{item.part.description}</span>;
                        }
                    },
                    minWidth: 160
                },
                {
                    Header: 'quantityHeader',
                    id: 'quantity',
                    accessor: (item: IinstallBasePartPopulated) => {
                        // each installBasePart should only have 1 quantity defined at a time
                        let count = 0;
                        if (item.jobPartQuantity) {
                            count++;
                        }
                        if (item.directPartQuantity) {
                            count++;
                        }
                        if (item.partStockQuantity) {
                            count++;
                        }
                        if (item.workOrderPartQuantity) {
                            count++;
                        }
                        if (count > 1) {
                            console.error('[columns -> accessor]: installBasePart has multiple quanitities.', item);
                        }
                        return (
                            <span>
                                {item.jobPartQuantity ||
                                    item.partStockQuantity ||
                                    item.workOrderPartQuantity ||
                                    item.directPartQuantity}
                            </span>
                        );
                    },
                    width: 100
                },
                {
                    Header: () => <span>{String(t('woNumber'))}</span>,
                    id: 'workOrder',
                    accessor: (item: IinstallBasePartPopulated) => {
                        if (item.workOrderPartID) {
                            const workOrderPart = workOrderParts[item.workOrderPartID];
                            if (workOrderPart && workOrderPart.workOrderID) {
                                const workOrder = workOrders[workOrderPart.workOrderID];
                                if (workOrder && workOrder.number) {
                                    return <span style={{ color: '#999' }}>{workOrder.number}</span>;
                                }
                            }
                        }
                        return <span></span>;
                    },
                    width: 120
                },
                {
                    Header: '',
                    id: 'delete',
                    minWidth: 20,
                    Cell: ({ row }: { row: any }) => {
                        // Hide delete button for all work order parts
                        if (row.original.workOrderPartID) {
                            return null;
                        }

                        return (
                            <Button
                                variant="link"
                                style={{
                                    float: 'right',
                                    color: constants.colors.red,
                                    display: disabled ? 'none' : 'block',
                                    paddingTop: '0',
                                    paddingBottom: '0',
                                    marginTop: '0',
                                    marginBottom: '0',
                                    lineHeight: '1',
                                    minHeight: 'auto',
                                    border: 'none'
                                }}
                                onClick={() => handleDelete(row.original)}
                            >
                                <FontAwesomeIcon icon={['far', 'times']} />
                            </Button>
                        );
                    }
                }
            ],
            t
        ) as Column<IinstallBasePartPopulated>[];
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [t, selectedJob.status, disabled]);

    // Use react-table v7 hooks with useFlexLayout
    const { getTableProps, getTableBodyProps, headerGroups, rows, prepareRow } = useTable<IinstallBasePartPopulated>(
        {
            columns,
            data: tableData as IinstallBasePartPopulated[]
        },
        useFlexLayout
    );

    // Handle row click
    const getTrProps = (rowInfo: any) => {
        if (rowInfo) {
            return {
                style: {
                    background:
                        rowInfo.index === selectedRow
                            ? constants.colors[`${currentTile.color}Tr` as keyof typeof constants.colors]
                            : ''
                },
                onClick: () => {
                    // Work order parts can only be edited while the job is actively being worked
                    // (New or InProgress). Once it's reopened/completed, the WO-part row is locked.
                    if (!isJobEditable(selectedJob) && rowInfo.original.workOrderPartID) {
                        return;
                    }
                    setSelectedRow(rowInfo.index);
                    setSelectedPartID(rowInfo.original.id);
                }
            };
        } else {
            return {};
        }
    };

    const tableClassName = `ReactTable beacon-table parts flex-layout -highlight ${currentTile.color}`;

    return (
        <div style={{ marginTop: '20px' }}>
            <div {...getTableProps()} className={tableClassName}>
                <div className="rt-thead -header">
                    {headerGroups.map(headerGroup => (
                        <div
                            {...headerGroup.getHeaderGroupProps()}
                            key={headerGroup.getHeaderGroupProps().key}
                            className="rt-tr"
                        >
                            {headerGroup.headers.map(column => (
                                <div {...column.getHeaderProps()} key={column.getHeaderProps().key} className="rt-th">
                                    {column.render('Header')}
                                </div>
                            ))}
                        </div>
                    ))}
                </div>
                <div {...getTableBodyProps()} className="rt-tbody">
                    {rows.map((row, i) => {
                        prepareRow(row);
                        const rowProps = getTrProps({ index: i, original: row.original });
                        return (
                            <div
                                {...row.getRowProps()}
                                key={row.getRowProps().key}
                                className={`rt-tr ${i % 2 === 1 ? '-odd' : '-even'}`}
                                onClick={rowProps.onClick}
                                style={rowProps.style}
                            >
                                {row.cells.map(cell => {
                                    return (
                                        <div {...cell.getCellProps()} key={cell.getCellProps().key} className="rt-td">
                                            {cell.render('Cell')}
                                        </div>
                                    );
                                })}
                            </div>
                        );
                    })}
                </div>
            </div>
            {tableData.length === 0 && (
                <div style={{ textAlign: 'center', padding: '20px' }}>{String(t('addPartTableNoDataText'))}</div>
            )}
        </div>
    );
};

export default PartTable;
